import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "overlay", "modal", "prevButton", "nextButton",
    "position", "beatInfo", "beatInfoButton", "beatChevron", "settingDisplay", "settingInput",
    "bodyDisplay", "bodyInput", "charactersDisplay", "locationsDisplay",
    "plotsDisplay", "characterCount", "locationCount", "plotCount",
    "characterForm", "characterList", "newCharacterFirst", "newCharacterLast",
    "locationForm", "locationList", "newLocationName",
    "plotForm", "plotList", "newPlotName",
    "beatDetailsPanel", "beatTitle", "beatDescription", "beatNote", "beatNoteContainer",
    "subBeatTitle", "subBeatDescription", "subBeatNote", "subBeatNoteContainer"
  ]

  static values = {
    projectId: Number,
    cardIds: Array,
    currentIndex: Number,
    currentCardId: Number,
    updateUrl: String
  }

  connect() {
    this.handleKeydown = this.handleKeydown.bind(this)
    document.addEventListener('keydown', this.handleKeydown)
  }

  disconnect() {
    document.removeEventListener('keydown', this.handleKeydown)
  }

  handleKeydown(event) {
    if (!this.isOpen()) return

    if (event.key === 'Escape') {
      this.close()
    } else if (event.key === 'ArrowLeft') {
      this.previous()
    } else if (event.key === 'ArrowRight') {
      this.next()
    }
  }

  isOpen() {
    return this.hasOverlayTarget && !this.overlayTarget.classList.contains('hidden')
  }

  open(event) {
    const cardId = event.currentTarget.dataset.cardId
    const index = this.cardIdsValue.indexOf(parseInt(cardId))

    if (index !== -1) {
      this.currentIndexValue = index
      this.currentCardIdValue = parseInt(cardId)
      this.loadCard(cardId)
      this.overlayTarget.classList.remove('hidden')
      document.body.style.overflow = 'hidden'
    }
  }

  // Start slideshow from the first card
  startSlideshow() {
    if (this.cardIdsValue.length === 0) return

    this.currentIndexValue = 0
    this.currentCardIdValue = this.cardIdsValue[0]
    this.loadCard(this.currentCardIdValue)
    this.overlayTarget.classList.remove('hidden')
    document.body.style.overflow = 'hidden'
  }

  close() {
    this.overlayTarget.classList.add('hidden')
    document.body.style.overflow = ''
    // Hide beat details panel when closing
    if (this.hasBeatDetailsPanelTarget) {
      this.beatDetailsPanelTarget.classList.add('hidden')
    }
  }

  toggleBeatDetails() {
    if (this.hasBeatDetailsPanelTarget) {
      const isHidden = this.beatDetailsPanelTarget.classList.toggle('hidden')
      // Rotate chevron icon
      if (this.hasBeatChevronTarget) {
        this.beatChevronTarget.style.transform = isHidden ? '' : 'rotate(180deg)'
      }
    }
  }

  previous() {
    if (this.currentIndexValue > 0) {
      this.currentIndexValue--
      this.currentCardIdValue = this.cardIdsValue[this.currentIndexValue]
      this.loadCard(this.currentCardIdValue)
    }
  }

  next() {
    if (this.currentIndexValue < this.cardIdsValue.length - 1) {
      this.currentIndexValue++
      this.currentCardIdValue = this.cardIdsValue[this.currentIndexValue]
      this.loadCard(this.currentCardIdValue)
    }
  }

  async loadCard(cardId, options = {}) {
    try {
      const response = await fetch(`/projects/${this.projectIdValue}/cards/${cardId}.json`, {
        headers: {
          'Accept': 'application/json'
        }
      })

      if (response.ok) {
        const data = await response.json()
        this.renderCard(data, options)
        this.updateNavigationButtons()
      }
    } catch (err) {
      console.error('Failed to load card:', err)
    }
  }

  renderCard(data, options = {}) {
    // Update position
    this.positionTarget.textContent = `${this.currentIndexValue + 1} of ${this.cardIdsValue.length}`

    // Update beat info button text
    const beatInfo = [data.beat_name, data.sub_beat_name].filter(Boolean).join(' > ')
    this.beatInfoTarget.textContent = beatInfo || 'No beat assigned'

    // Update beat details panel
    if (this.hasBeatTitleTarget && data.beat_name) {
      this.beatTitleTarget.textContent = data.beat_name
    }
    if (this.hasBeatDescriptionTarget) {
      if (data.beat_description) {
        this.beatDescriptionTarget.innerHTML = data.beat_description
        this.beatDescriptionTarget.classList.remove('hidden')
      } else {
        this.beatDescriptionTarget.innerHTML = ''
        this.beatDescriptionTarget.classList.add('hidden')
      }
    }
    if (this.hasBeatNoteTarget && this.hasBeatNoteContainerTarget) {
      if (data.beat_note) {
        this.beatNoteTarget.textContent = data.beat_note
        this.beatNoteContainerTarget.classList.remove('hidden')
      } else {
        this.beatNoteContainerTarget.classList.add('hidden')
      }
    }

    // Update sub-beat details
    if (this.hasSubBeatTitleTarget && data.sub_beat_name) {
      this.subBeatTitleTarget.textContent = data.sub_beat_name
    }
    if (this.hasSubBeatDescriptionTarget) {
      if (data.sub_beat_description) {
        this.subBeatDescriptionTarget.innerHTML = data.sub_beat_description
        this.subBeatDescriptionTarget.classList.remove('hidden')
      } else {
        this.subBeatDescriptionTarget.innerHTML = ''
        this.subBeatDescriptionTarget.classList.add('hidden')
      }
    }
    if (this.hasSubBeatNoteTarget && this.hasSubBeatNoteContainerTarget) {
      if (data.sub_beat_note) {
        this.subBeatNoteTarget.textContent = data.sub_beat_note
        this.subBeatNoteContainerTarget.classList.remove('hidden')
      } else {
        this.subBeatNoteContainerTarget.classList.add('hidden')
      }
    }

    // Hide beat details panel and reset chevron when loading new card
    if (this.hasBeatDetailsPanelTarget) {
      this.beatDetailsPanelTarget.classList.add('hidden')
    }
    if (this.hasBeatChevronTarget) {
      this.beatChevronTarget.style.transform = ''
    }

    // Update setting/title
    if (data.setting) {
      this.settingDisplayTarget.textContent = data.setting
      this.settingDisplayTarget.classList.remove('text-gray-300')
      this.settingDisplayTarget.classList.add('text-gray-800')
    } else {
      this.settingDisplayTarget.textContent = 'Card Title'
      this.settingDisplayTarget.classList.remove('text-gray-800')
      this.settingDisplayTarget.classList.add('text-gray-300')
    }
    this.settingInputTarget.value = data.setting || ''

    // Update body
    this.bodyDisplayTarget.textContent = data.body || 'Click to add content...'
    this.bodyInputTarget.value = data.body || ''

    // Store current IDs for selection tracking
    this.currentCharacterIds = data.characters?.map(c => c.id) || []
    this.currentLocationIds = data.locations?.map(l => l.id) || []
    this.currentPlotIds = data.plots?.map(p => p.id) || []

    // Update characters
    this.renderTags(this.charactersDisplayTarget, data.characters, 'bg-pink-100 text-pink-700 border border-pink-200')
    if (this.hasCharacterCountTarget) {
      this.characterCountTarget.textContent = data.characters?.length || ''
    }

    // Update locations
    this.renderTags(this.locationsDisplayTarget, data.locations, 'bg-orange-100 text-orange-700 border border-orange-200')
    if (this.hasLocationCountTarget) {
      this.locationCountTarget.textContent = data.locations?.length || ''
    }

    // Update plots
    this.renderTags(this.plotsDisplayTarget, data.plots, 'bg-amber-100 text-amber-700 border border-amber-200')
    if (this.hasPlotCountTarget) {
      this.plotCountTarget.textContent = data.plots?.length || ''
    }

    // Store update URL
    this.updateUrlValue = `/projects/${this.projectIdValue}/cards/${data.id}`

    // Close any open forms when loading new card (unless keepFormsOpen is set)
    if (!options.keepFormsOpen) {
      this.closeAllForms()
    }

    // Reinitialize Lucide icons
    if (window.lucide) {
      window.lucide.createIcons()
    }
  }

  renderTags(container, items, colorClass) {
    // Keep the icon (Lucide replaces <i> with <svg>), remove other children
    const icon = container.querySelector('svg') || container.querySelector('i')
    container.innerHTML = ''
    if (icon) container.appendChild(icon)

    if (items && items.length > 0) {
      items.forEach(item => {
        const tag = document.createElement('span')
        tag.className = `px-3 py-1 rounded-full text-sm font-medium ${colorClass}`
        tag.textContent = item.name || item.full_name
        container.appendChild(tag)
      })
    }
  }

  updateNavigationButtons() {
    if (this.hasPrevButtonTarget) {
      this.prevButtonTarget.disabled = this.currentIndexValue === 0
      this.prevButtonTarget.classList.toggle('opacity-50', this.currentIndexValue === 0)
    }
    if (this.hasNextButtonTarget) {
      this.nextButtonTarget.disabled = this.currentIndexValue === this.cardIdsValue.length - 1
      this.nextButtonTarget.classList.toggle('opacity-50', this.currentIndexValue === this.cardIdsValue.length - 1)
    }
  }

  // Inline editing for setting
  editSetting() {
    this.settingDisplayTarget.classList.add('hidden')
    this.settingInputTarget.classList.remove('hidden')
    this.settingInputTarget.focus()
  }

  async saveSetting(event) {
    if (event.type === 'keydown' && event.key !== 'Enter') return

    const value = this.settingInputTarget.value.trim()
    if (value) {
      this.settingDisplayTarget.textContent = value
      this.settingDisplayTarget.classList.remove('text-gray-300')
      this.settingDisplayTarget.classList.add('text-gray-800')
    } else {
      this.settingDisplayTarget.textContent = 'Card Title'
      this.settingDisplayTarget.classList.remove('text-gray-800')
      this.settingDisplayTarget.classList.add('text-gray-300')
    }
    this.settingDisplayTarget.classList.remove('hidden')
    this.settingInputTarget.classList.add('hidden')

    await this.updateCard({ setting: value })
  }

  // Inline editing for body
  editBody() {
    this.bodyDisplayTarget.classList.add('hidden')
    this.bodyInputTarget.classList.remove('hidden')
    this.bodyInputTarget.focus()
  }

  async saveBody() {
    const value = this.bodyInputTarget.value.trim()
    this.bodyDisplayTarget.textContent = value || 'Click to add content...'
    this.bodyDisplayTarget.classList.remove('hidden')
    this.bodyInputTarget.classList.add('hidden')

    await this.updateCard({ body: value })
  }

  async updateCard(data) {
    try {
      const response = await fetch(this.updateUrlValue, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
          'Accept': 'application/json'
        },
        body: JSON.stringify({ card: data })
      })

      if (!response.ok) {
        console.error('Failed to update card')
      }
    } catch (err) {
      console.error('Failed to update card:', err)
    }
  }

  // Form toggle methods
  toggleCharacterForm() {
    this.closeAllForms()
    this.characterFormTarget.classList.toggle('hidden')
    if (!this.characterFormTarget.classList.contains('hidden')) {
      this.loadCharacterList()
    }
  }

  toggleLocationForm() {
    this.closeAllForms()
    this.locationFormTarget.classList.toggle('hidden')
    if (!this.locationFormTarget.classList.contains('hidden')) {
      this.loadLocationList()
    }
  }

  togglePlotForm() {
    this.closeAllForms()
    this.plotFormTarget.classList.toggle('hidden')
    if (!this.plotFormTarget.classList.contains('hidden')) {
      this.loadPlotList()
    }
  }

  closeAllForms() {
    if (this.hasCharacterFormTarget) this.characterFormTarget.classList.add('hidden')
    if (this.hasLocationFormTarget) this.locationFormTarget.classList.add('hidden')
    if (this.hasPlotFormTarget) this.plotFormTarget.classList.add('hidden')
  }

  // Load lists for selection
  async loadCharacterList() {
    try {
      const response = await fetch(`/projects/${this.projectIdValue}/characters.json`)
      if (response.ok) {
        const characters = await response.json()
        this.renderCharacterList(characters)
      }
    } catch (err) {
      console.error('Failed to load characters:', err)
    }
  }

  async loadLocationList() {
    try {
      const response = await fetch(`/projects/${this.projectIdValue}/locations.json`)
      if (response.ok) {
        const locations = await response.json()
        this.renderLocationList(locations)
      }
    } catch (err) {
      console.error('Failed to load locations:', err)
    }
  }

  async loadPlotList() {
    try {
      const response = await fetch(`/projects/${this.projectIdValue}/plots.json`)
      if (response.ok) {
        const plots = await response.json()
        this.renderPlotList(plots)
      }
    } catch (err) {
      console.error('Failed to load plots:', err)
    }
  }

  renderCharacterList(characters) {
    const container = this.characterListTarget
    container.innerHTML = ''

    characters.forEach(character => {
      const isSelected = this.currentCharacterIds?.includes(character.id)
      const button = document.createElement('button')
      button.type = 'button'
      button.className = `w-full text-left px-3 py-2 rounded-lg transition mb-1 ${isSelected ? 'bg-emerald-500 hover:bg-emerald-600 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`
      button.textContent = character.full_name
      button.dataset.characterId = character.id
      button.addEventListener('click', () => this.toggleCharacter(character.id, button))
      container.appendChild(button)
    })
  }

  renderLocationList(locations) {
    const container = this.locationListTarget
    container.innerHTML = ''

    locations.forEach(location => {
      const isSelected = this.currentLocationIds?.includes(location.id)
      const button = document.createElement('button')
      button.type = 'button'
      button.className = `w-full text-left px-3 py-2 rounded-lg transition mb-1 ${isSelected ? 'bg-emerald-500 hover:bg-emerald-600 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`
      button.textContent = location.name
      button.dataset.locationId = location.id
      button.addEventListener('click', () => this.toggleLocation(location.id, button))
      container.appendChild(button)
    })
  }

  renderPlotList(plots) {
    const container = this.plotListTarget
    container.innerHTML = ''

    plots.forEach(plot => {
      const isSelected = this.currentPlotIds?.includes(plot.id)
      const button = document.createElement('button')
      button.type = 'button'
      button.className = `w-full text-left px-3 py-2 rounded-lg transition mb-1 ${isSelected ? 'bg-emerald-500 hover:bg-emerald-600 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`
      button.textContent = plot.name
      button.dataset.plotId = plot.id
      button.addEventListener('click', () => this.togglePlot(plot.id, button))
      container.appendChild(button)
    })
  }

  // Toggle associations
  async toggleCharacter(characterId, button) {
    try {
      const response = await fetch(`/projects/${this.projectIdValue}/cards/${this.currentCardIdValue}/toggle_character?character_id=${characterId}`, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
          'Accept': 'application/json'
        }
      })

      if (response.ok) {
        const data = await response.json()
        this.toggleButtonStyle(button)
        this.updateCharacterIds(characterId)
        this.loadCard(this.currentCardIdValue, { keepFormsOpen: true }) // Refresh display, keep form open
      }
    } catch (err) {
      console.error('Failed to toggle character:', err)
    }
  }

  async toggleLocation(locationId, button) {
    try {
      const response = await fetch(`/projects/${this.projectIdValue}/cards/${this.currentCardIdValue}/toggle_location?location_id=${locationId}`, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
          'Accept': 'application/json'
        }
      })

      if (response.ok) {
        this.toggleButtonStyle(button)
        this.updateLocationIds(locationId)
        this.loadCard(this.currentCardIdValue, { keepFormsOpen: true })
      }
    } catch (err) {
      console.error('Failed to toggle location:', err)
    }
  }

  async togglePlot(plotId, button) {
    try {
      const response = await fetch(`/projects/${this.projectIdValue}/cards/${this.currentCardIdValue}/toggle_plot?plot_id=${plotId}`, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
          'Accept': 'application/json'
        }
      })

      if (response.ok) {
        this.toggleButtonStyle(button)
        this.updatePlotIds(plotId)
        this.loadCard(this.currentCardIdValue, { keepFormsOpen: true })
      }
    } catch (err) {
      console.error('Failed to toggle plot:', err)
    }
  }

  toggleButtonStyle(button) {
    if (!button) return

    if (button.classList.contains('bg-emerald-500')) {
      button.classList.remove('bg-emerald-500', 'hover:bg-emerald-600', 'text-white')
      button.classList.add('bg-gray-100', 'hover:bg-gray-200', 'text-gray-700')
    } else {
      button.classList.remove('bg-gray-100', 'hover:bg-gray-200', 'text-gray-700')
      button.classList.add('bg-emerald-500', 'hover:bg-emerald-600', 'text-white')
    }
  }

  updateCharacterIds(id) {
    if (!this.currentCharacterIds) this.currentCharacterIds = []
    const index = this.currentCharacterIds.indexOf(id)
    if (index > -1) {
      this.currentCharacterIds.splice(index, 1)
    } else {
      this.currentCharacterIds.push(id)
    }
  }

  updateLocationIds(id) {
    if (!this.currentLocationIds) this.currentLocationIds = []
    const index = this.currentLocationIds.indexOf(id)
    if (index > -1) {
      this.currentLocationIds.splice(index, 1)
    } else {
      this.currentLocationIds.push(id)
    }
  }

  updatePlotIds(id) {
    if (!this.currentPlotIds) this.currentPlotIds = []
    const index = this.currentPlotIds.indexOf(id)
    if (index > -1) {
      this.currentPlotIds.splice(index, 1)
    } else {
      this.currentPlotIds.push(id)
    }
  }

  // Create new items
  async createCharacter() {
    const firstName = this.newCharacterFirstTarget.value.trim()
    const lastName = this.newCharacterLastTarget.value.trim()

    if (!firstName) return

    try {
      const response = await fetch(`/projects/${this.projectIdValue}/characters`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
          'Accept': 'application/json'
        },
        body: JSON.stringify({ character: { first_name: firstName, last_name: lastName } })
      })

      if (response.ok) {
        const data = await response.json()
        this.newCharacterFirstTarget.value = ''
        this.newCharacterLastTarget.value = ''
        // Add to card - toggleCharacter will keep form open via keepFormsOpen
        await this.toggleCharacter(data.character.id, null)
        // Refresh list to show the new character as selected
        this.loadCharacterList()
      }
    } catch (err) {
      console.error('Failed to create character:', err)
    }
  }

  async createLocation() {
    const name = this.newLocationNameTarget.value.trim()

    if (!name) return

    try {
      const response = await fetch(`/projects/${this.projectIdValue}/locations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
          'Accept': 'application/json'
        },
        body: JSON.stringify({ location: { name: name } })
      })

      if (response.ok) {
        const data = await response.json()
        this.newLocationNameTarget.value = ''
        await this.toggleLocation(data.location.id, null)
        this.loadLocationList()
      }
    } catch (err) {
      console.error('Failed to create location:', err)
    }
  }

  async createPlot() {
    const name = this.newPlotNameTarget.value.trim()

    if (!name) return

    try {
      const response = await fetch(`/projects/${this.projectIdValue}/plots`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
          'Accept': 'application/json'
        },
        body: JSON.stringify({ plot: { name: name } })
      })

      if (response.ok) {
        const data = await response.json()
        this.newPlotNameTarget.value = ''
        await this.togglePlot(data.plot.id, null)
        this.loadPlotList()
      }
    } catch (err) {
      console.error('Failed to create plot:', err)
    }
  }
}
